import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import MarketingHeader from "@/components/MarketingHeader";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <MarketingHeader showSignIn={false} />
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
            Reset password
          </div>
          <h2 className="font-display text-3xl font-semibold mt-3 tracking-tight">
            Set a new password
          </h2>

          {!token ? (
            <div
              data-testid="reset-missing-token"
              className="mt-6 rounded-md border border-amber-400/20 bg-amber-400/5 p-4 text-sm text-amber-200 flex gap-3"
            >
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.8} />
              <div>
                <div className="font-medium">Missing reset token</div>
                <div className="mt-1 text-amber-200/80">
                  Open the reset link from the email you received, or{" "}
                  <Link to="/forgot-password" className="underline text-amber-100">
                    request a new one
                  </Link>
                  .
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-3">
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="New password (8+ chars)"
                data-testid="reset-password-input"
                required
                minLength={8}
                className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2.5 text-sm"
              />
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                data-testid="reset-password-confirm"
                required
                minLength={8}
                className="w-full bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2.5 text-sm"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-submit-button"
                className="w-full flex items-center justify-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors py-3 rounded-md font-medium text-sm disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                ) : (
                  <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                )}
                Update password
              </button>
            </form>
          )}

          <p className="mt-6 text-xs text-neutral-600 leading-relaxed">
            <Link
              to="/login"
              data-testid="reset-link-login"
              className="text-white hover:underline"
            >
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
